import React, { useEffect, useState } from "react";
import MainContainer from "../../layout/MainContainer";
import {
  Box,
  Button,
  Card,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import { isValidInput } from "../../utils/validations";
import { useLazyGetPurchasedSubscriptionsQuery } from "../../services/subscription";
import { PurchasedSubscription } from "../../types/General";
import { Loader, showError } from "../../constants";
import SkeletonTable from "../../components/SkeletonTable";
import useAuth from "../../hooks/useAuth";
import useTranslation from "../../hooks/Translation";
import Pagination from "../../components/Pagination";
import NoDataFound from "../../components/NoDataFound";
import {
  PERMISSION_MODULES,
  getPagePermissionFlags,
} from "../../constants/permissionModules";
import moment from "moment";

const subscriptionStatusLabel = (
  status: number | string | undefined,
  translate: any,
) => {
  switch (Number(status)) {
    case 0:
      return translate?.subscription?.cancelled || "Cancelled";
    case 1:
      return translate?.subscription?.active || "Active";
    case 2:
      return translate?.subscription?.expired || "Expired";
    default:
      return status ?? "-";
  }
};

const PurchasedSubscriptions = () => {
  const navigate = useNavigate();
  const userData = useAuth();
  const translate = useTranslation();
  const [getPurchasedQuery] = useLazyGetPurchasedSubscriptionsQuery();

  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [rows, setRows] = useState<PurchasedSubscription[]>([]);
  const [hidePermission, setHidePermission] = useState({
    isAdd: true,
    isDelete: true,
    isView: true,
  });

  const totalPages = Math.ceil(totalCount / 10) || 1;

  const checkPermission = () => {
    setHidePermission(
      getPagePermissionFlags(
        userData?.permission,
        PERMISSION_MODULES.MANAGE_SUBSCRIPTION_PLANS,
        userData?.role === "ADMIN",
      ),
    );
  };

  const fetchPurchases = async () => {
    setLoading(true);
    try {
      const response = await getPurchasedQuery({
        page,
        query: debouncedSearchTerm.trim(),
        status: statusFilter,
      }).unwrap();
      if (response?.statusCode === 200) {
        const list =
          response?.data?.subscribedPlans ||
          response?.data?.susbcribedPlans ||
          [];
        setRows(list);
        setTotalCount(response?.data?.count || 0);
      } else {
        setRows([]);
        setTotalCount(0);
      }
    } catch (error: any) {
      showError(error?.data?.message || "");
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userData) {
      checkPermission();
    }
  }, [userData]);

  useEffect(() => {
    fetchPurchases();
  }, [page, debouncedSearchTerm, statusFilter]);

  if (!hidePermission?.isView && userData?.role !== "ADMIN") {
    return null;
  }

  return (
    <MainContainer>
      <Loader isLoad={loading} isLinear />
      <div className="main_loyout">
        <div className="dashboard">
          <h1 className="mn_hdng">
            {translate?.main?.PurchasedSubscriptions ||
              "Purchased Subscriptions"}
          </h1>
          <Button
            className="btn btn_primary"
            onClick={() => navigate("/manage-subscription")}
          >
            {translate?.main?.ManageSubscriptionPlan || "Manage Plans"}
          </Button>
        </div>
        <Card className="cards">
          <Box className="cards_header">
            <SearchBar
              value={searchTerm}
              searchTerm={searchTerm}
              onCross={() => setSearchTerm("")}
              setDebouncedSearchTerm={setDebouncedSearchTerm}
              onChange={(val: any) => {
                if (isValidInput(val.target.value)) {
                  setSearchTerm(val.target.value);
                  setPage(1);
                }
              }}
            />
            <Box className="cards_header_right">
              <TextField
                select
                size="small"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setPage(1);
                }}
                sx={{ minWidth: 160 }}
                label={translate?.main?.Status || "Status"}
              >
                <MenuItem value="">
                  {translate?.main?.All || "All"}
                </MenuItem>
                <MenuItem value="1">
                  {translate?.subscription?.active || "Active"}
                </MenuItem>
                <MenuItem value="2">
                  {translate?.subscription?.expired || "Expired"}
                </MenuItem>
                <MenuItem value="0">
                  {translate?.subscription?.cancelled || "Cancelled"}
                </MenuItem>
              </TextField>
            </Box>
          </Box>
          <TableContainer className="table_container">
            <Table sx={{ minWidth: 900 }} aria-label="purchased subscriptions">
              <TableHead>
                <TableRow>
                  <TableCell align="center">
                    {translate?.main?.SNo || "S.No"}
                  </TableCell>
                      <TableCell>
                    {translate?.user?.Name || "User"}
                  </TableCell>
                  <TableCell>
                    {translate?.main?.Email || "Email"}
                  </TableCell>
                  <TableCell>
                    {translate?.main?.PlanName || "Plan"}
                  </TableCell>
                  <TableCell>
                    {translate?.main?.Price || "Price"}
                  </TableCell>
                  <TableCell>
                    {translate?.main?.PurchaseDate || "Purchase Date"}
                  </TableCell>
                  <TableCell>
                    {translate?.main?.ExpiryDate || "Expiry"}
                  </TableCell>
                  <TableCell>
                    {translate?.main?.Status || "Status"}
                  </TableCell>
                  <TableCell>
                    {translate?.payment?.transactionId || "Transaction ID"}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <SkeletonTable columns={9} rows={10} />
                ) : rows?.length > 0 ? (
                  rows.map((row, i) => (
                    <TableRow key={row._id}>
                      <TableCell align="center">
                        {(page - 1) * 10 + i + 1}
                      </TableCell>
                      <TableCell>
                        {row?.user?.fullName || "-"}
                        {row?.user?.role ? (
                          <Box
                            component="span"
                            sx={{
                              display: "block",
                              fontSize: 12,
                              color: "text.secondary",
                              textTransform: "capitalize",
                            }}
                          >
                            {row.user.role}
                          </Box>
                        ) : null}
                      </TableCell>
                      <TableCell>{row?.user?.email || "-"}</TableCell>
                      <TableCell>
                        {row?.planName ||
                          row?.membershipPlan?.planName ||
                          "-"}
                      </TableCell>
                      <TableCell>
                        {Number.isFinite(Number(row?.price))
                          ? `${Number(row.price).toLocaleString("en-IN")} KD`
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {row?.createdAt
                          ? moment(row.createdAt).format("DD MMM YYYY, hh:mm A")
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {row?.expiryDate
                          ? moment(row.expiryDate).format("DD MMM YYYY")
                          : "-"}
                      </TableCell>
                      <TableCell>
                        {subscriptionStatusLabel(row?.status, translate)}
                      </TableCell>
                      <TableCell>{row?.transactionId || "-"}</TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <NoDataFound
                        message={
                          translate?.subscription?.noPurchases ||
                          "No purchased subscriptions found"
                        }
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
        {rows?.length > 0 ? (
          <Pagination
            setPage={setPage}
            module={rows}
            page={page}
            onPageChange={(newPage: number) => setPage(newPage)}
            totalPages={totalPages}
          />
        ) : null}
      </div>
    </MainContainer>
  );
};

export default PurchasedSubscriptions;
